import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import { localeOrigin } from "@/lib/locale";
import { friendlyError } from "@/lib/friendly-errors";
import { trackOperational } from "@/lib/track";
import {
  getMyUpgradeOptions,
  previewMyStripeUpgrade,
  upgradeMyStripeMembership,
  startMyPaypalUpgrade,
} from "@/lib/membership-upgrade.functions";

type Options = Awaited<ReturnType<typeof getMyUpgradeOptions>>;

const PENDING_KEY = "rt_paypal_upgrade_plan";

function money(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export const Route = createFileRoute("/plan-change")({
  validateSearch: (search: Record<string, unknown>): { plan?: string } => ({
    plan: typeof search.plan === "string" ? search.plan : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Confirm your plan change | Recovery Trauma™" },
      {
        name: "description",
        content:
          "Review your Recovery Trauma™ plan change — the plan you are moving to and the exact amount — before anything is charged.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: PlanChangePage,
});

function PlanChangePage() {
  const { plan } = Route.useSearch();
  const navigate = useNavigate();
  const load = useServerFn(getMyUpgradeOptions);
  const previewCharge = useServerFn(previewMyStripeUpgrade);
  const upgradeStripe = useServerFn(upgradeMyStripeMembership);
  const startPaypal = useServerFn(startMyPaypalUpgrade);

  const [data, setData] = useState<Options | null>(null);
  const [loading, setLoading] = useState(true);
  const [todayCents, setTodayCents] = useState<number | null>(null);
  const [todayLoading, setTodayLoading] = useState(false);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    trackOperational("upgrade_confirmation_opened", {
      plan_id: plan ?? null,
      surface: "page",
      at: new Date().toISOString(),
    });
    load({ data: undefined as never })
      .then((d) => {
        if (cancelled) return;
        setData(d);
        const opt = d?.options.find((o) => o.id === plan);
        if (opt && d.provider === "stripe") {
          setTodayLoading(true);
          previewCharge({ data: { plan_id: opt.id as "membership_6month" } })
            .then((r) => {
              if (!cancelled) setTodayCents(r?.amount_cents ?? null);
            })
            .catch(() => {
              if (!cancelled) setTodayCents(null);
            })
            .finally(() => {
              if (!cancelled) setTodayLoading(false);
            });
        }
      })
      .catch(() => setData(null))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const option = data?.options.find((o) => o.id === plan) ?? null;

  function goBack(via: string) {
    trackOperational("upgrade_confirmation_cancelled", {
      plan_id: plan ?? null,
      surface: "page",
      via,
      at: new Date().toISOString(),
    });
    void navigate({ to: "/billing" });
  }

  async function confirm() {
    if (!data || !option || pending) return;
    setPending(true);
    trackOperational("upgrade_payment_confirmed", {
      plan_id: option.id,
      provider: data.provider,
      amount_today_cents: todayCents,
      surface: "page",
      at: new Date().toISOString(),
    });
    try {
      if (data.provider === "stripe") {
        const r = await upgradeStripe({ data: { plan_id: option.id as "membership_6month" } });
        if (r.ok) {
          trackOperational("upgrade_payment_successful", { plan_id: option.id, provider: "stripe" });
          toast.success("Your membership has been changed", {
            description: r.current_period_end
              ? `Your previous plan has stopped. Your next renewal is ${formatDate(r.current_period_end)}.`
              : "Your previous plan has stopped.",
            duration: 9000,
          });
          void navigate({ to: "/billing" });
        } else {
          trackOperational("upgrade_payment_failed", { plan_id: option.id, provider: "stripe" });
          toast.error("Plan change not completed", { description: r.message, duration: 9000 });
        }
        return;
      }

      const origin = localeOrigin();
      const r = await startPaypal({
        data: {
          plan_id: option.id as "membership_6month",
          return_url: `${origin}/billing?upgrade=paypal`,
          cancel_url: `${origin}/billing?upgrade=cancelled`,
        },
      });
      if (!r.ok) {
        trackOperational("upgrade_payment_failed", { plan_id: option.id, provider: "paypal" });
        toast.error("Plan change not completed", { description: r.message, duration: 9000 });
        return;
      }
      if (r.approve_url) {
        window.sessionStorage.setItem(PENDING_KEY, option.id);
        window.location.href = r.approve_url;
        return;
      }
      trackOperational("upgrade_payment_successful", { plan_id: option.id, provider: "paypal" });
      toast.success("Your membership has been changed", {
        description: "Your previous plan has stopped.",
        duration: 9000,
      });
      void navigate({ to: "/billing" });
    } catch (err) {
      trackOperational("upgrade_payment_failed", { plan_id: option?.id ?? null, error: true });
      toast.error("Plan change not completed", { description: friendlyError(err) });
    } finally {
      setPending(false);
    }
  }

  if (loading) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-2xl items-center justify-center px-4">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data || !option) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16">
        <div className="rounded-3xl border border-border bg-card p-8 text-center">
          <h1 className="font-serif text-2xl text-foreground">
            We couldn't find that plan change
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Nothing has been changed and nothing has been charged. You can go back to your
            membership and choose again.
          </p>
          <Link to="/billing" className="btn-gold mt-6 inline-flex items-center gap-2 text-sm">
            Back to your membership
          </Link>
        </div>
      </div>
    );
  }

  const nextRenewal = formatDate(data.next_renewal);

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 md:py-16">
      <button
        type="button"
        onClick={() => goBack("back_link")}
        className="mb-5 inline-flex min-h-[44px] items-center gap-2 text-sm text-muted-foreground underline"
      >
        <ArrowLeft className="h-4 w-4" /> Back to your membership
      </button>

      <div className="rounded-3xl border border-gold/40 bg-card p-6 shadow-sm md:p-8">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-6 w-6 text-[var(--gold-deep)]" strokeWidth={1.5} />
          <div>
            <h1 className="font-serif text-2xl text-foreground md:text-3xl">
              Confirm your plan change
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Nothing has been changed or charged yet. Please read this page, then confirm only if
              it's what you want.
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-3 rounded-2xl border border-border bg-muted/20 p-4 text-sm">
          <div>
            <p className="text-muted-foreground">You are changing from</p>
            <p className="text-foreground">
              <strong>{data.current_plan_label}</strong>
              {data.current_plan_price_cents != null
                ? ` — ${money(data.current_plan_price_cents)}${
                    data.current_plan_billing_note ? ` (${data.current_plan_billing_note})` : ""
                  }`
                : ""}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">To</p>
            <p className="text-foreground">
              <strong>{option.name}</strong> — {money(option.price_cents)}
              {option.billing_note ? ` (${option.billing_note})` : ""}
            </p>
          </div>
          {nextRenewal ? (
            <p className="text-xs text-muted-foreground">
              Your current renewal date is {nextRenewal}.
            </p>
          ) : null}
        </div>

        <div className="mt-4 rounded-2xl border border-gold/40 bg-muted/10 p-4 text-sm">
          {data.provider === "stripe" ? (
            <>
              <p className="text-foreground">
                <strong>Amount charged today: </strong>
                {todayLoading ? (
                  <span className="text-muted-foreground">working it out…</span>
                ) : todayCents != null ? (
                  money(todayCents)
                ) : (
                  <span className="text-muted-foreground">
                    {money(option.price_cents)}, less any unused time left on your current plan
                  </span>
                )}
              </p>
              {todayCents != null && todayCents < option.price_cents ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  This is lower than the full price because the unused time on your current plan has
                  been credited.
                </p>
              ) : null}
              <p className="mt-2 text-muted-foreground">
                Your saved card is only charged when you press Confirm below.
              </p>
            </>
          ) : (
            <>
              <p className="text-foreground">
                <strong>Amount PayPal will take: </strong>
                {money(option.price_cents)} {option.billing_note ? `(${option.billing_note})` : ""}
              </p>
              <p className="mt-2 text-muted-foreground">
                You'll be taken to PayPal to approve this change. Nothing is taken from your PayPal
                account until you approve it there.
              </p>
            </>
          )}
        </div>

        <p className="mt-4 text-sm text-muted-foreground">
          Your {data.current_plan_label} plan stops billing once this goes through. Your journal,
          reflections and everything else in your account stay exactly as they are. If you go back,
          nothing changes.
        </p>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row-reverse sm:items-center">
          <button
            type="button"
            onClick={() => void confirm()}
            disabled={pending || todayLoading}
            className="btn-gold inline-flex min-h-[52px] w-full items-center justify-center gap-2 text-sm disabled:opacity-60 sm:w-auto"
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {data.provider === "stripe"
              ? pending
                ? "Processing…"
                : `Confirm & Pay${todayCents != null ? ` ${money(todayCents)}` : ""}`
              : pending
              ? "Opening PayPal…"
              : "Continue to PayPal"}
          </button>
          <button
            type="button"
            onClick={() => goBack("go_back")}
            disabled={pending}
            className="inline-flex min-h-[52px] w-full items-center justify-center rounded-xl border border-border bg-background px-5 text-sm font-medium text-foreground transition hover:bg-muted/40 disabled:opacity-60 sm:w-auto"
          >
            Go back, don't change anything
          </button>
        </div>
      </div>
    </div>
  );
}
